/**
 * The consent link for a Client (Story 3): shown once, as a URL and a QR
 * code, right after it is minted or re-minted. Lifted from the Clients
 * page so the Defender integration page can re-mint too (task 69).
 */

import { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';

import type { CreateTenantResult } from '@bluesphere/api-client';
import { PillButton } from '@bluesphere/ui-kit';

import { ModalShell } from '@bluesphere/mdr-alert-panel';

export function InviteModal({
  tenant,
  onClose,
}: {
  tenant: CreateTenantResult;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  // A Client created without MDR has no consent link (task 224): nothing to show.
  const consentUrl = tenant.consent_url ?? '';

  async function copy() {
    try {
      await navigator.clipboard.writeText(consentUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard API can fail in non-secure contexts; do nothing */
    }
  }

  if (!consentUrl) return null;

  return (
    <ModalShell onClose={onClose} width="max-w-xl">
      <div className="flex items-start justify-between gap-4 border-b border-bluesphere-border px-6 py-5">
        <div>
          <h2 className="text-lg font-semibold text-bluesphere-textOnLight">
            Invite ready for <span className="font-mono">{tenant.display_name}</span>
          </h2>
          <p className="mt-1 text-sm text-bluesphere-textSecondary">
            Share this link or QR code with the Client admin. They sign in to their Microsoft tenant
            and grant consent.
            <br />
            <strong className="font-semibold">
              This is the only time we display the link.
            </strong>{' '}
            Copy or screenshot it before closing.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="px-2 text-2xl leading-none text-bluesphere-muted hover:text-bluesphere-textOnLight"
          aria-label="Close"
        >
          ×
        </button>
      </div>

      <div className="space-y-4 px-6 py-5">
        <div className="flex items-center justify-center rounded-input border border-bluesphere-border bg-white p-4">
          <QRCodeSVG value={consentUrl} size={220} level="M" includeMargin />
        </div>
        <div>
          <div className="mb-1 text-xs uppercase tracking-wider text-bluesphere-textSecondary">
            Consent URL
          </div>
          <div className="flex items-stretch gap-2">
            <input
              readOnly
              value={consentUrl}
              onFocus={(e) => e.currentTarget.select()}
              className="flex-1 rounded-input border border-bluesphere-borderMuted px-3 py-2 font-mono text-xs text-bluesphere-textOnLight"
            />
            <PillButton size="sm" onClick={copy}>
              {copied ? 'Copied' : 'Copy'}
            </PillButton>
          </div>
        </div>
        <div className="rounded-input border border-bluesphere-border bg-bluesphere-surfaceMuted px-3 py-2 text-xs text-bluesphere-textSecondary">
          The Client admin needs Global Administrator or Privileged Role Administrator in their
          tenant: the consent is for Microsoft Graph application permissions
          (SecurityAlert.Read.All, SecurityIncident.Read.All, ThreatHunting.Read.All). They accept;
          we get a callback; this row flips to <strong>Connected</strong>.
        </div>
      </div>

      <div className="flex items-center justify-end border-t border-bluesphere-border px-6 py-3">
        <PillButton variant="ghost" size="sm" onClick={onClose}>
          Done
        </PillButton>
      </div>
    </ModalShell>
  );
}
